import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PageContainer from "../../../components/dashboard/PageContainer";
import BasicInformationForm from "./BasicInformationForm";
import SetAvatarLocationForm from "./SetAvatarLocationForm";
import SetNamePreferenceForm from "./SetNamePreferenceForm";
import PreviewAndPublish from "./PreviewAndPublish";
import FormButton from "../../../components/dashboard/FormButton";
import FormTabs, { formSteps } from "./FormTabs";
import { uploadEventImage } from "../../../utils/s3-images";
import { useMutation } from "@apollo/client";
import { CREATE_EVENT } from "../../../server/graphql/mutations/create-event-banner";
import {
  validateForms,
  getCurrentStepFields,
} from "../../../helpers/validation";
import { initialCreateEventState } from "../../../helpers/events";

const CurrentStep = ({ activeStep, formData, setFormData, handleChange }) => {
  switch (activeStep) {
    case 0:
      return (
        <BasicInformationForm
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
        />
      );
    case 1:
      return (
        <SetAvatarLocationForm
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
        />
      );
    case 2:
      return (
        <SetNamePreferenceForm
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
        />
      );
    case 3:
      return (
        <PreviewAndPublish formData={formData} handleChange={handleChange} />
      );
    default:
      return (
        <BasicInformationForm formData={formData} handleChange={handleChange} />
      );
  }
};

const NewEvent = () => {
  const navigate = useNavigate();
  const [
    createEvent,
    { data: newEvent, loading: creating, error: createError },
  ] = useMutation(CREATE_EVENT);
  const [activeStepIsValid, setActiveStepIsValid] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const isLastStep = activeStep === formSteps.length - 1;
  const [formData, setFormData] = useState(initialCreateEventState);

  const [uploading, setUploading] = useState(false);
  const [cloudUrls, setCloudUrls] = useState({
    imageUrl: null,
    bannerUrl: null,
  });
  // TODO handle errors
  const [errors, setErrors] = useState({ imageUrl: null, bannerUrl: null });

  const nextStep = () => setActiveStep((prevState) => prevState + 1);

  const prevStep = () => setActiveStep((prevState) => prevState - 1);

  useEffect(() => {
    (async () => {
      const isValid = await validateForms(
        activeStep,
        getCurrentStepFields(formData)[activeStep]
      );
      setActiveStepIsValid(isValid);
    })();
  }, [activeStep, JSON.stringify(formData)]);

  const validateAndContinue = async () => {
    try {
      const isValid = await validateForms(
        activeStep,
        getCurrentStepFields(formData)[activeStep]
      );

      if (!isValid) return;
      nextStep();
    } catch (error) {
      toast.error(error?.message ?? "Ensure all inputs are valid!");
    }
  };

  const handleOnChange = (e) => {
    let { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);

      const [mainResult, bannerResult] = await Promise.all([
        uploadEventImage(formData.imageFile, 'main'),
        uploadEventImage(formData.bannerFile, 'banner'),
      ]);

      setCloudUrls({
        imageUrl: mainResult.url,
        bannerUrl: bannerResult.url,
      });

      toast.success("Images uploaded successfully!");
    } catch (error) {
      toast.error("Error uploading event images.");
      console.error("Error uploading event images.", error);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    try {
      if (cloudUrls.imageUrl && cloudUrls.bannerUrl) {
        // send to server
        createEvent({
          variables: {
            title: formData.title,
            subtitle: formData.subtitle,
            description: formData.description,
            imageUrl: cloudUrls.imageUrl,
            bannerUrl: cloudUrls.bannerUrl,
            url: formData.url,
            eventDate: new Date(formData.eventDate ?? "").toString(),
            tags: formData.tags,
            visible: formData.visibility.toLocaleLowerCase() === "public",
            imageAttribute: {
              ...formData.imageAttribute,
              x: Math.round(formData.imageAttribute.x),
              y: Math.round(formData.imageAttribute.y),
              width: Math.round(formData.imageAttribute.width),
              height: Math.round(formData.imageAttribute.height),
            },
            nameAttribute: !formData.hideUsername
              ? {
                  ...formData.nameAttribute,
                  fontSize: formData.nameAttribute.fontSize.value,
                  fontFamily: formData.nameAttribute.fontFamily.value,
                  x: Math.round(formData.nameAttribute.x),
                  y: Math.round(formData.nameAttribute.y),
                  width: Math.round(formData.nameAttribute.width),
                  height: Math.round(formData.nameAttribute.height),
                }
              : null,
          },
        });
      }
    } catch (error) {
      console.error("Error publishing event banner.", error);
    }
  }, [cloudUrls]);

  useEffect(() => {
    if (newEvent?.createEvent?.uuid) {
      toast.success("Event Created Successfully!");
      navigate("/dashboard/events");
    } else {
      toast.error("Event Creation Failed!");
    }
  }, [newEvent]);

  return (
    <PageContainer title="Create a New Event">
      <section>
        <div className="form-wrapper bg-white my-3 px-4 py-0 rounded-lg">
          <FormTabs activeStep={activeStep} formData={formData} />
          <CurrentStep
            activeStep={activeStep}
            formData={formData}
            setFormData={setFormData}
            handleChange={handleOnChange}
          />

          <div className="cta-buttons mt-8 flex">
            {activeStep !== 0 && (
              <FormButton
                disabled={uploading || creating}
                onClick={prevStep}
                classes="mr-3"
              >
                Previous
              </FormButton>
            )}

            {isLastStep ? (
              <FormButton
                onClick={handleSubmit}
                disabled={uploading || creating}
              >
                {uploading || creating ? "Please wait..." : "Publish Now"}
              </FormButton>
            ) : (
              <FormButton
                onClick={validateAndContinue}
                disabled={!activeStepIsValid}
              >
                Next
              </FormButton>
            )}
          </div>
        </div>
      </section>
    </PageContainer>
  );
};

export default NewEvent;
